// Report-param grammar for the answer-variants family, ported from the coffee block, its chart
// view and the controls strip above them.
//
// Four params carry the block's state, addressed by prefix:
//   cm   the chart's mode -- or the marker saying the word cloud is showing instead of a chart
//   z    the chart's zoom (shared with the other views of the question)
//   s    the sort order (shared with the other views of the question)
//   cb   which answer variants are open, as a dot-joined list of positions
//
// `cm` is overloaded on purpose, the way the coffee block left it: a question showing its variants
// as a word cloud has no chart mode to remember, so the one param says which of the two is on
// screen. `off` is the marker for a question that offers the cloud by default and was turned back.

import { isEqual } from 'lodash'

import { hasBase } from '@aytm/app/stats/page/question-views/answer-variants/components/chart/lib/chartModes'
import { isRType as calcIsRType } from '@aytm/app/stats/page/components/lib/utils'
import { DEFAULT_SORT_ORDER, resolveSortOrder } from '@aytm/app/stats/page/question-views/components/lib/sort-param'
import { decodeUncollapsedParam } from '@aytm/app/stats/page/question-views/components/lib/uncollapsed-param'
import { decodeZoomParam } from '@aytm/app/stats/page/question-views/components/lib/zoom-param'
export { calcSortParamsSection, resolveSortOrder } from '@aytm/app/stats/page/question-views/components/lib/sort-param'
import {
  WC_CHART_MODE,
  PERCENT_CHART_MODES,
  isWcChartMode,
  calcQuestionChartMode
} from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

export { DEFAULT_SORT_ORDER }

export { WC_CHART_MODE, isWcChartMode }

// A zoom that matches the one the whole report is set to is not the question's own, so the key
// drops out of the persisted set.
export { calcZoomSection } from '@aytm/app/stats/page/question-views/components/lib/zoom-param'

// Which of the two the block opens on. A question that offers the cloud by default shows it until
// the researcher says otherwise, and `off` is how saying otherwise is written down.
export const resolveChartMode = ({ cm } = {}, format = {}) => {
  if (WC_CHART_MODE === cm) return WC_CHART_MODE
  if (null == cm && WC_CHART_MODE === format?.chartMode) return WC_CHART_MODE

  return cm || format?.chartMode || null
}

// The chart-mode space collapses to percents for a question with a base, or one told to show
// percents only; a question counting respondents has no pie to slice.
export const calcDefaultChartMode = ({
  chartMode,
  questionChartMode,
  globalChartMode,
  answer,
  percentsOnly,
  type
} = {}) => {
  let mode = calcQuestionChartMode(chartMode, questionChartMode, globalChartMode)

  if (calcIsRType({ type }) && 'pie' === mode) mode = 'respondents'

  if ((hasBase(answer) || true === percentsOnly) && !PERCENT_CHART_MODES.includes(mode)) return 'percents'

  return mode
}

// A chart mode that says what the question already says is not worth storing -- except when the
// question says "cloud" and the researcher wants a chart, which is what there is no silence for.
export const calcChartParamsSection = ({ chartMode, questionChartMode, format } = {}) => {
  const stored = format?.chartMode || questionChartMode || 'off'

  if (isWcChartMode(stored) && !isWcChartMode(chartMode)) return { cm: 'off' }

  return { cm: isEqual(chartMode, stored) ? null : chartMode }
}

// The state a block opens in, read off the params bag it is handed: every key by its own rule.
export const resolveReportParams = ({ params = {}, format, defaultSortOrder } = {}) => ({
  chartMode: resolveChartMode(params, format),
  zoomLevel: decodeZoomParam(params.z),
  sortOrder: resolveSortOrder(params.s, defaultSortOrder),
  uncollapsed: decodeUncollapsedParam(params.cb)
})
